import { type FC } from "react";

import { type BlackboardInstance } from "../blackboardModels";
import { formatTokenCount } from "./helpers";

// ---------------------------------------------------------------------------
// TokenStackBar — proportional stacked bar: cache-hit input | uncached | output
// ---------------------------------------------------------------------------

/**
 * Lifted verbatim from the former InfoTab god component. Cache write tokens
 * are shown separately in the legend — they are write credits, not part of
 * the input/output balance. Segments give instant gestalt of the cache hit
 * ratio vs raw usage.
 */
export const TokenStackBar: FC<{
  tokenUsage: BlackboardInstance["tokenUsage"];
}> = ({ tokenUsage }) => {
  const { cachedInputTokens, cacheWriteTokens, inputTokens, outputTokens } =
    tokenUsage;
  const total = inputTokens + outputTokens;

  if (total === 0) {
    return (
      <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
        —
      </span>
    );
  }

  const uncachedInput = Math.max(0, inputTokens - cachedInputTokens);
  const cachedPct = (cachedInputTokens / total) * 100;
  const uncachedPct = (uncachedInput / total) * 100;
  const outputPct = (outputTokens / total) * 100;
  const cacheHitRatio =
    inputTokens > 0
      ? Math.round((cachedInputTokens / inputTokens) * 100)
      : null;

  return (
    <div className="flex w-full flex-col gap-2">
      {cacheHitRatio !== null && (
        <div className="flex items-baseline gap-1.5">
          <span className="font-mono text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
            {cacheHitRatio}%
          </span>
          <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
            cache hit
          </span>
        </div>
      )}
      <div className="flex h-2 w-full overflow-hidden bg-slate-100 dark:bg-slate-800">
        {cachedPct > 0 && (
          <div
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${cachedPct}%` }}
          />
        )}
        {uncachedPct > 0 && (
          <div
            className="h-full bg-sky-400/60 transition-all duration-500"
            style={{ width: `${uncachedPct}%` }}
          />
        )}
        {outputPct > 0 && (
          <div
            className="h-full bg-violet-500/70 transition-all duration-500"
            style={{ width: `${outputPct}%` }}
          />
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <TokenLegendItem
          label="CACHE HIT"
          value={cachedInputTokens}
          colorClass="bg-emerald-500"
        />
        <TokenLegendItem
          label="INPUT"
          value={uncachedInput}
          colorClass="bg-sky-400/60"
        />
        <TokenLegendItem
          label="OUTPUT"
          value={outputTokens}
          colorClass="bg-violet-500/70"
        />
        {cacheWriteTokens > 0 && (
          <TokenLegendItem
            label="WRITE"
            value={cacheWriteTokens}
            colorClass="bg-amber-400"
          />
        )}
      </div>
    </div>
  );
};

const TokenLegendItem: FC<{
  label: string;
  value: number;
  colorClass: string;
}> = ({ label, value, colorClass }) => (
  <div className="flex items-center gap-1.5">
    <span className={`h-1.5 w-1.5 flex-shrink-0 ${colorClass}`} />
    <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
      {label}
    </span>
    <span className="font-mono text-[10px] font-bold tabular-nums text-slate-600 dark:text-slate-300">
      {formatTokenCount(value)}
    </span>
  </div>
);
